import React, { useState, useMemo, useEffect } from "react";
import { Search, Filter, ChevronDown, ChevronUp, X, CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { DateRange } from "react-day-picker";
import { Input, controlSizes } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { categoryDisplayLabel } from "./ClassificationOverrideSelects";

export interface TransactionFilters {
  search: string;
  transaction_type: "all" | "credit" | "debit";
  sub_category: string;
  tag: string;
  account_name: string;
  account_number: string;
  date_from: string;
  date_to: string;
  amount_min: string;
  amount_max: string;
}

export interface ActiveFilter {
  type: keyof TransactionFilters;
  value: string;
  label: string;
}

export const defaultFilters: TransactionFilters = {
  search: "",
  transaction_type: "all",
  sub_category: "all",
  tag: "",
  account_name: "",
  account_number: "",
  date_from: "",
  date_to: "",
  amount_min: "",
  amount_max: "",
};

const REMOVE_MAP: Partial<Record<keyof TransactionFilters, Partial<TransactionFilters>>> = {
  date_from: { date_from: "", date_to: "" },
  amount_min: { amount_min: "", amount_max: "" },
  transaction_type: { transaction_type: "all" },
  sub_category: { sub_category: "all" },
};

interface TransactionFiltersProps {
  filters: TransactionFilters;
  onFiltersChange: (filters: TransactionFilters) => void;
  subCategories?: string[];
  accountNames?: string[];
  accountNumbers?: string[];
  tags?: string[];
}

const DateRangePicker: React.FC<{
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
}> = ({ dateFrom, dateTo, onChange }) => {
  const [open, setOpen] = useState(false);

  const range: DateRange | undefined =
    dateFrom || dateTo
      ? {
          from: dateFrom ? parseISO(dateFrom) : undefined,
          to: dateTo ? parseISO(dateTo) : undefined,
        }
      : undefined;

  const handleSelect = (selected: DateRange | undefined) => {
    if (!selected) {
      onChange("", "");
      return;
    }
    const from = selected.from ? format(selected.from, "yyyy-MM-dd") : "";
    const to = selected.to ? format(selected.to, "yyyy-MM-dd") : "";
    onChange(from, to);
    // Close only when a full range is selected
    if (selected.from && selected.to) setOpen(false);
  };

  const label =
    dateFrom && dateTo
      ? `${format(parseISO(dateFrom), "MMM d, yyyy")} – ${format(parseISO(dateTo), "MMM d, yyyy")}`
      : dateFrom
        ? `From ${format(parseISO(dateFrom), "MMM d, yyyy")}`
        : "Pick a date range";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            controlSizes.default,
            "w-full justify-start gap-2 text-left font-normal",
            !dateFrom && !dateTo && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0" />
          <span className="truncate">{label}</span>
          {(dateFrom || dateTo) && (
            <X
              className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onChange("", "");
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={range}
          onSelect={handleSelect}
          numberOfMonths={2}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

const TransactionFiltersComponent: React.FC<TransactionFiltersProps> = ({
  filters,
  onFiltersChange,
  subCategories = [],
  accountNames = [],
  accountNumbers = [],
  tags = [],
}) => {
  const [expanded, setExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const set = (key: keyof TransactionFilters, value: string) =>
    onFiltersChange({ ...filters, [key]: value });

  const applySearch = () => onFiltersChange({ ...filters, search: searchInput.trim() });

  const clearSearch = () => {
    setSearchInput("");
    onFiltersChange({ ...filters, search: "" });
  };

  const removeFilter = (type: keyof TransactionFilters) =>
    onFiltersChange({ ...filters, ...(REMOVE_MAP[type] ?? { [type]: "" }) });

  const handleClear = () => {
    setSearchInput("");
    onFiltersChange(defaultFilters);
  };

  const activeFilters = useMemo((): ActiveFilter[] => {
    const a: ActiveFilter[] = [];
    if (filters.search)
      a.push({ type: "search", value: filters.search, label: `Text: "${filters.search}"` });
    if (filters.account_name)
      a.push({
        type: "account_name",
        value: filters.account_name,
        label: `Account: ${filters.account_name}`,
      });
    if (filters.account_number)
      a.push({
        type: "account_number",
        value: filters.account_number,
        label: `Acct #: ${filters.account_number}`,
      });
    if (filters.tag) a.push({ type: "tag", value: filters.tag, label: `Tag: ${filters.tag}` });
    if (filters.transaction_type !== "all")
      a.push({
        type: "transaction_type",
        value: filters.transaction_type,
        label: `Type: ${filters.transaction_type}`,
      });
    if (filters.sub_category && filters.sub_category !== "all")
      a.push({
        type: "sub_category",
        value: filters.sub_category,
        label: `Category: ${categoryDisplayLabel(filters.sub_category)}`,
      });
    if (filters.date_from || filters.date_to) {
      const label =
        filters.date_from && filters.date_to
          ? `Date: ${filters.date_from} – ${filters.date_to}`
          : filters.date_from
            ? `From: ${filters.date_from}`
            : `To: ${filters.date_to}`;
      a.push({ type: "date_from", value: filters.date_from, label });
    }
    if (filters.amount_min || filters.amount_max) {
      const label =
        filters.amount_min && filters.amount_max
          ? `Amount: $${filters.amount_min} – $${filters.amount_max}`
          : filters.amount_min
            ? `Min: $${filters.amount_min}`
            : `Max: $${filters.amount_max}`;
      a.push({ type: "amount_min", value: filters.amount_min, label });
    }
    return a;
  }, [filters]);

  return (
    <div className="mb-4 rounded-lg border bg-card p-4 shadow-sm">
      {activeFilters.length > 0 && (
        <div className="mb-4">
          <span className="mr-2 text-xs text-muted-foreground">Active Filters:</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {activeFilters.map((f) => (
              <Badge key={f.type} variant="outline" className="gap-1 pr-1">
                {f.label}
                <button
                  type="button"
                  className="rounded-sm p-0.5 hover:bg-muted"
                  onClick={() => removeFilter(f.type)}
                  aria-label={`Remove ${f.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={handleClear}>
              Clear All
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 items-end gap-3">
        <div className="col-span-12 md:col-span-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9 pr-9"
              placeholder="Search description, memo, tags..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applySearch()}
            />
            {searchInput && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:bg-muted"
                onClick={clearSearch}
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="col-span-6 md:col-span-2">
          <Label className="sr-only">Type</Label>
          <Select
            value={filters.transaction_type}
            onValueChange={(v) => set("transaction_type", v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="credit">Credits</SelectItem>
              <SelectItem value="debit">Debits</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-6 md:col-span-2">
          <Label className="sr-only">Category</Label>
          <Select value={filters.sub_category} onValueChange={(v) => set("sub_category", v)}>
            <SelectTrigger>
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {subCategories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {categoryDisplayLabel(cat)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-6 md:col-span-2">
          <Button
            type="button"
            variant="outline"
            className={cn(controlSizes.default, "w-full gap-2")}
            onClick={() => setExpanded(!expanded)}
          >
            <Filter className="h-4 w-4" />
            More
            {activeFilters.length > 0 && (
              <Badge variant="secondary" className="h-5 min-w-5 px-1 text-[0.65rem]">
                {activeFilters.length}
              </Badge>
            )}
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        <div className="col-span-6 md:col-span-2">
          <Button
            type="button"
            variant="ghost"
            className={cn(controlSizes.default, "w-full")}
            onClick={handleClear}
            disabled={activeFilters.length === 0}
          >
            Clear All
          </Button>
        </div>
      </div>

      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleContent>
          <div className="mt-4 border-t pt-4">
            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-12">
              <div className="md:col-span-3">
                <Label className="mb-1.5 block text-xs">Account Name</Label>
                <Select
                  value={filters.account_name || "__all__"}
                  onValueChange={(v) => set("account_name", v === "__all__" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Accounts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Accounts</SelectItem>
                    {accountNames.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label className="mb-1.5 block text-xs">Account Number</Label>
                <Select
                  value={filters.account_number || "__all__"}
                  onValueChange={(v) => set("account_number", v === "__all__" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Numbers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Numbers</SelectItem>
                    {accountNumbers.map((num) => (
                      <SelectItem key={num} value={num}>
                        {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-6">
                <Label className="mb-1.5 block text-xs">Tag</Label>
                <Select
                  value={filters.tag || "__all__"}
                  onValueChange={(v) => set("tag", v === "__all__" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Tags" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Tags</SelectItem>
                    {tags.map((tag) => (
                      <SelectItem key={tag} value={tag}>
                        {tag.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block text-xs">Date Range</Label>
                <DateRangePicker
                  dateFrom={filters.date_from}
                  dateTo={filters.date_to}
                  onChange={(from, to) => {
                    onFiltersChange({ ...filters, date_from: from, date_to: to });
                  }}
                />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">Min Amount</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    $
                  </span>
                  <Input
                    type="number"
                    className="pl-7"
                    value={filters.amount_min}
                    onChange={(e) => set("amount_min", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">Max Amount</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    $
                  </span>
                  <Input
                    type="number"
                    className="pl-7"
                    value={filters.amount_max}
                    onChange={(e) => set("amount_max", e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default TransactionFiltersComponent;
